import { z } from 'zod'
import {
  type ReceptionInput,
  type ReceptionPart,
  type ReceptionPartSupplier,
  type ReceptionPartType,
  type ReceptionProvider,
  type ReceptionServing,
  type ReceptionSupplierCategory,
  receptionPartTypes,
  receptionSchema,
  WELCOME_TOAST_PART_ID,
} from './reception'
import { supplierPlanSchema } from './supplier-plan'

const legacySupplier = supplierPlanSchema.safeExtend({ weddingSupplierId: z.uuid().optional() })
const legacySupplierRow = legacySupplier.safeExtend({
  id: z.uuid(),
  service: z.string().trim().max(200),
})
/** The fixed-slot reception plan stored before food and drink became a list of parts. */
export const legacyReceptionSchema = z.object({
  banquet: z.boolean(),
  buffet: z.boolean(),
  soup: z.boolean(),
  mainCourse: z.boolean(),
  dessert: z.boolean(),
  cateringProvider: z.enum(['undecided', 'venue', 'caterer']),
  catering: legacySupplier,
  welcomeToast: z.boolean(),
  welcomeToastTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .or(z.literal('')),
  beforeCeremony: z.boolean(),
  alcohol: z.enum(['undecided', 'open', 'limited', 'none']),
  barProvider: z.enum(['undecided', 'venue', 'caterer', 'supplier']),
  bar: legacySupplier,
  cakeProvider: z.enum(['undecided', 'venue', 'caterer', 'sweet-bar']),
  cake: legacySupplier,
  cakeBooked: z.boolean(),
  sweetBar: z.array(legacySupplierRow).max(50),
  others: z.array(legacySupplierRow).max(50),
  /** Older plans predate page notes. */
  notes: z.string().trim().max(5000).default(''),
})
export type LegacyReception = z.infer<typeof legacyReceptionSchema>
type LegacySupplier = z.infer<typeof legacySupplier>

/** Stored plans and history snapshots hold either shape; a legacy plan has no `parts`. */
export const persistedReceptionSchema = z.union([receptionSchema, legacyReceptionSchema])
export type PersistedReception = z.infer<typeof persistedReceptionSchema>
export function isLegacyReception(plan: PersistedReception): plan is LegacyReception {
  return !('parts' in plan)
}

/**
 * Text written into converted plans. It becomes stored plan content, like any text the
 * planner typed, so it is persisted once and never re-rendered from this table.
 */
const text = {
  soup: 'Polévka',
  mainCourse: 'Hlavní chod',
  dessert: 'Dezert',
  agreement: 'Dohoda: ',
  contact: 'Kontakt: ',
  earlierContact: 'Dříve uvedený kontakt: ',
  cakeBooked: 'Označeno jako objednané',
  moneyPrefix: 'Dříve zadáno v plánu: ',
  moneySuffix: '. Sledujte v Rozpočtu.',
  price: 'cena ',
  paid: 'zaplaceno ',
  currency: ' Kč',
  payment: { cash: 'hotově', card: 'kartou', transfer: 'převodem' },
} as const

/**
 * Names of converted parts, stored like the text above. They equal the planner's starter
 * labels for the same types, which the planner's tests check.
 */
export const legacyReceptionPartNames = {
  toast: 'Uvítací přípitek',
  beforeCeremony: 'Občerstvení před obřadem',
  banquet: 'Svatební hostina',
  reception: 'Večerní raut',
  cake: 'Dort',
  sweetBar: 'Sladký bar',
  bar: 'Bar a pití',
  custom: 'Vlastní pohoštění',
} as const satisfies Partial<Record<ReceptionPartType, string>>
const names = legacyReceptionPartNames

/** How the API resolved a legacy free-text supplier to a My suppliers entry. */
export type LegacySupplierLink = {
  weddingSupplierId: string
  created: boolean
  /** The entry already holds the legacy contact, so the part notes need not repeat it. */
  contactStored: boolean
}
export type LegacyReceptionOptions = {
  newId: () => string
  /** Keyed by the legacy supplier id: `catering`, `bar`, `cake` or a row id. */
  links?: Readonly<Record<string, LegacySupplierLink>>
}
/** A legacy supplier that conversion links to My suppliers, by its stored link or its name. */
export type LegacyReceptionSupplier = {
  id: string
  weddingSupplierId: string | null
  name: string
  contact: string
  category: ReceptionSupplierCategory
  booked: boolean
}

type Source = { id: string; supplier: LegacySupplier }

const cateringProvider = { undecided: null, venue: 'venue', caterer: 'supplier' } as const
const cakeProvider = {
  undecided: null,
  venue: 'venue',
  caterer: 'supplier',
  'sweet-bar': 'supplier',
} as const
const barProvider = {
  undecided: null,
  venue: 'venue',
  caterer: 'supplier',
  supplier: 'supplier',
} as const

function hasData(supplier: LegacySupplier) {
  return Boolean(
    supplier.name.trim() ||
      supplier.weddingSupplierId ||
      supplier.contact.trim() ||
      supplier.arrival ||
      supplier.amount ||
      supplier.paidAmount ||
      (supplier.paymentMethod && supplier.paymentMethod !== 'undecided') ||
      supplier.agreement?.trim(),
  )
}

function money(amount: number) {
  return `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${text.currency}`
}

function supplierNotes(supplier: LegacySupplier, link: LegacySupplierLink | undefined) {
  const notes: string[] = []
  const agreement = supplier.agreement?.trim()
  if (agreement) notes.push(`${text.agreement}${agreement}`)
  const paid = [
    supplier.amount ? `${text.price}${money(supplier.amount)}` : '',
    supplier.paidAmount ? `${text.paid}${money(supplier.paidAmount)}` : '',
    supplier.paymentMethod && supplier.paymentMethod !== 'undecided'
      ? text.payment[supplier.paymentMethod]
      : '',
  ].filter(Boolean)
  if (paid.length) notes.push(`${text.moneyPrefix}${paid.join(', ')}${text.moneySuffix}`)
  const contact = supplier.contact.trim()
  if (contact && !link?.contactStored)
    notes.push(`${link ? text.earlierContact : text.contact}${contact}`)
  return notes
}

function convert(legacy: LegacyReception, { newId, links = {} }: LegacyReceptionOptions) {
  const parts: ReceptionPart[] = []
  const firstSources: (Source & { part: ReceptionPart })[] = []
  const used = new Set<string>()

  const add = (
    type: ReceptionPartType,
    name: string,
    fields: Partial<ReceptionPart> = {},
    sources: Source[] = [],
    provider: ReceptionProvider | null = null,
    extraNotes: string[] = [],
  ) => {
    const suppliers: ReceptionPartSupplier[] = []
    const notes: string[] = []
    const first: Source[] = []
    for (const source of sources) {
      if (!hasData(source.supplier)) continue
      const weddingSupplierId =
        source.supplier.weddingSupplierId ?? links[source.id]?.weddingSupplierId
      const row = {
        name: source.supplier.name.trim(),
        ...(weddingSupplierId ? { weddingSupplierId } : {}),
      }
      // The arrival event and notes stay with the first part that holds the supplier.
      if (used.has(source.id)) {
        suppliers.push({ id: newId(), ...row, arrival: '' })
        continue
      }
      used.add(source.id)
      first.push(source)
      suppliers.push({ id: source.id, ...row, arrival: source.supplier.arrival ?? '' })
      notes.push(...supplierNotes(source.supplier, links[source.id]))
    }
    const part: ReceptionPart = {
      id: newId(),
      type,
      name,
      time: '',
      timeEnd: '',
      place: '',
      guestVisible: false,
      provider: provider ?? (suppliers.length ? 'supplier' : null),
      suppliers,
      serving: null,
      lines: [],
      notes: [...notes, ...extraNotes].join('\n'),
      alcohol: null,
      drinks: null,
      ...fields,
    }
    parts.push(part)
    for (const source of first) firstSources.push({ ...source, part })
    return part
  }
  const lines = (...values: (string | false)[]) =>
    values.filter((value) => value !== false).map((value) => ({ id: newId(), text: value }))
  const slot = (id: 'catering' | 'bar' | 'cake'): Source => ({ id, supplier: legacy[id] })
  const row = (value: LegacyReception['sweetBar'][number]): Source => ({
    id: value.id,
    supplier: value,
  })

  if (legacy.beforeCeremony) add('beforeCeremony', names.beforeCeremony)
  if (legacy.welcomeToast)
    add('toast', names.toast, {
      id: WELCOME_TOAST_PART_ID,
      time: legacy.welcomeToastTime,
      guestVisible: true,
    })

  const meal = (
    type: 'banquet' | 'reception',
    name: string,
    serving: ReceptionServing | null,
    courses: ReceptionPart['lines'] = [],
  ) =>
    add(
      type,
      name,
      { serving, lines: courses },
      [slot('catering')],
      cateringProvider[legacy.cateringProvider],
    )
  const banquet = legacy.banquet ? meal('banquet', names.banquet, 'table') : undefined
  const buffet = legacy.buffet ? meal('reception', names.reception, 'buffet') : undefined
  const courses = lines(
    legacy.soup && text.soup,
    legacy.mainCourse && text.mainCourse,
    legacy.dessert && text.dessert,
  )
  const courseHolder = banquet ?? buffet
  if (courseHolder) courseHolder.lines.push(...courses)
  else if (courses.length || legacy.cateringProvider !== 'undecided' || hasData(legacy.catering))
    meal('banquet', names.banquet, null, courses)

  if (legacy.cakeProvider !== 'undecided' || legacy.cakeBooked || hasData(legacy.cake))
    add(
      'cake',
      names.cake,
      {},
      legacy.cakeProvider === 'caterer' ? [slot('catering'), slot('cake')] : [slot('cake')],
      cakeProvider[legacy.cakeProvider],
      // A cake supplier created during conversion is created as booked instead.
      legacy.cakeBooked && !links.cake?.created ? [text.cakeBooked] : [],
    )

  for (const value of legacy.sweetBar)
    add('sweetBar', value.service || names.sweetBar, {}, [row(value)])

  if (legacy.alcohol !== 'undecided' || legacy.barProvider !== 'undecided' || hasData(legacy.bar))
    add(
      'bar',
      names.bar,
      { alcohol: legacy.alcohol === 'undecided' ? null : legacy.alcohol },
      legacy.barProvider === 'caterer' ? [slot('catering'), slot('bar')] : [slot('bar')],
      barProvider[legacy.barProvider],
    )

  for (const value of legacy.others)
    add('custom', value.service || value.name.trim() || names.custom, {}, [row(value)])

  return {
    plan: { parts, notes: legacy.notes, hiddenPromptCodes: [] } satisfies ReceptionInput,
    firstSources,
  }
}

/**
 * Turns a legacy plan into parts without losing what was entered. Supplier rows keep the
 * legacy ids so Harmonogram overrides survive; money and agreements become part notes.
 */
export function legacyReceptionToParts(
  legacy: LegacyReception,
  options: LegacyReceptionOptions,
): ReceptionInput {
  return convert(legacy, options).plan
}

/** Linked or named legacy suppliers that conversion should link to, or create in, My suppliers. */
export function legacyReceptionSuppliers(legacy: LegacyReception): LegacyReceptionSupplier[] {
  let counter = 0
  return convert(legacy, { newId: () => `${counter++}` })
    .firstSources.filter(({ supplier }) => supplier.name.trim() || supplier.weddingSupplierId)
    .map(({ id, supplier, part }) => ({
      id,
      weddingSupplierId: supplier.weddingSupplierId ?? null,
      name: supplier.name.trim(),
      contact: supplier.contact.trim(),
      category: receptionPartTypes[part.type].category,
      booked: id === 'cake' && legacy.cakeBooked,
    }))
}
